import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, ArrowRight, CheckCircle2, ExternalLink, Loader2, RefreshCw, ShieldCheck } from 'lucide-react'
import { usePublicBooking } from '@/stores/booking.store'
import { useGetCheckoutSessionStatus, usePublicInvoice } from '@/stores/invoice.store'
import { clearPaymentSessionContext, getPaymentSessionContext } from '@/lib/payment-session-context'
import { setToken } from '@/stores/token.store'
import { captureError, trackPaymentEvent } from '@/lib/sentry'
import { formatCents } from '@/lib/utils'
import { getCustomerFacingInvoicePaymentTotals } from '@/lib/invoice-payment-totals'
import { PublicInvoice } from '@/gen/cortado/v1/inquiries_pb'
import { CheckoutSessionStatusEnum, PaymentScheduleStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { Button } from '@/components/ui/button'
import { SkeletonBar } from '@/components/skeleton-bar'
import { PortalHeader } from '@/components/portal-header'
import { PortalFooter } from '@/components/portal-footer'
import { hasUnansweredQuestionnaireItems } from '@/lib/booking-questionnaire'
import { paymentSuccessBookingSearch } from '@/lib/booking-search'

const SESSION_POLL_INTERVAL_MS = 2500
const SESSION_SETTLE_TIMEOUT_MS = 45000
const VERIFICATION_POLL_INTERVAL_MS = 5000

type ProcessingSearchParams = {
  token?: string
  session_id?: string
  invoice_id?: string
  schedule_id?: string
  verification_url?: string
  tip_only?: boolean
}

type ProcessingState = 'processing' | 'success' | 'submitted' | 'failed' | 'expired' | 'verification' | 'unavailable'

export const Route = createFileRoute('/booking/$id/processing')({
  component: BookingProcessingPage,
  validateSearch: (search: Record<string, unknown>): ProcessingSearchParams => ({
    token: typeof search.token === 'string' ? search.token : undefined,
    session_id: typeof search.session_id === 'string' ? search.session_id : undefined,
    invoice_id: typeof search.invoice_id === 'string' ? search.invoice_id : undefined,
    schedule_id: typeof search.schedule_id === 'string' ? search.schedule_id : undefined,
    verification_url: typeof search.verification_url === 'string' ? search.verification_url : undefined,
    tip_only: search.tip_only === true || search.tip_only === 'true' ? true : undefined,
  }),
})

function BookingProcessingPage() {
  const { id } = useParams({ from: '/booking/$id/processing' })
  const navigate = useNavigate()
  const search = useSearch({ from: '/booking/$id/processing' })
  const restoredContext = useMemo(() => {
    return search.session_id ? getPaymentSessionContext(search.session_id) : null
  }, [search.session_id])

  const invoiceId = search.invoice_id ?? restoredContext?.invoiceId
  const scheduleId = search.schedule_id ?? restoredContext?.scheduleId
  const tipOnly = !!search.tip_only
  const { booking, isLoading: isBookingLoading, hasLoaded: hasBookingLoaded } = usePublicBooking(id)
  const { invoice, isLoading: isInvoiceLoading, refetch } = usePublicInvoice(invoiceId ?? '')
  const { getCheckoutSessionStatus } = useGetCheckoutSessionStatus()
  const [state, setState] = useState<ProcessingState>(search.verification_url ? 'verification' : 'processing')
  const [statusText, setStatusText] = useState('Contacting payment provider...')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const startedAtRef = useRef(Date.now())
  const successNavigateStartedRef = useRef(false)

  useEffect(() => {
    if (search.token) {
      setToken(id, search.token)
      navigate({
        to: '/booking/$id/processing',
        params: { id },
        search: {
          session_id: search.session_id,
          invoice_id: invoiceId,
          schedule_id: scheduleId,
          verification_url: search.verification_url,
          tip_only: search.tip_only,
        },
        replace: true,
      })
      return
    }
  }, [id, invoiceId, navigate, scheduleId, search.session_id, search.tip_only, search.token, search.verification_url])

  const schedule = useMemo(() => {
    return invoice?.paymentSchedules?.find(item => item.id === scheduleId) ?? null
  }, [invoice?.paymentSchedules, scheduleId])

  const amountDueCents = useMemo(() => {
    return schedule ? getScheduleAmountDue(schedule) : invoice?.balanceDueCents ?? 0n
  }, [invoice?.balanceDueCents, schedule])

  useEffect(() => {
    // Tip-only mode has no schedule_id; only the invoice is required there.
    if (search.session_id && (!invoiceId || (!tipOnly && !scheduleId))) {
      setState('unavailable')
      setStatusText('We could not restore the invoice details for this payment session. Return to your booking and refresh payment status.')
    }
  }, [invoiceId, scheduleId, search.session_id, tipOnly])

  const refreshInvoice = useCallback(async () => {
    if (!invoiceId) return null
    const result = await refetch()
    return result.data?.invoice ?? null
  }, [invoiceId, refetch])

  useEffect(() => {
    // Base payment polling keys on a schedule; the tip-only flow keys on the invoice.
    if (!search.session_id || !invoiceId || (!tipOnly && !scheduleId)) return

    let isMounted = true
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const poll = async () => {
      try {
        const [sessionStatus, updatedInvoice] = await Promise.all([
          getCheckoutSessionStatus(search.session_id!),
          refreshInvoice(),
        ])
        if (!isMounted) return

        const updatedSchedule = updatedInvoice?.paymentSchedules?.find(item => item.id === scheduleId)
        const paymentStatus = sessionStatus.paymentStatus?.toLowerCase() ?? ''

        // Tip-only success: the carrier schedule stays Paid throughout, so we cannot
        // use schedule status. The tip is recorded when invoice.tip_due_cents hits 0.
        if (tipOnly) {
          if (updatedInvoice && updatedInvoice.tipDueCents === 0n) {
            setState('success')
            setStatusText('Tip confirmed.')
            trackPaymentEvent('succeeded', { sessionId: search.session_id, invoiceId, tipOnly })
            clearPaymentSessionContext(search.session_id!)
            return
          }
        } else if (updatedSchedule?.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAID) {
          setState('success')
          setStatusText('Payment confirmed.')
          trackPaymentEvent('succeeded', { sessionId: search.session_id, invoiceId, scheduleId })
          clearPaymentSessionContext(search.session_id!)
          return
        }

        if (
          sessionStatus.status === CheckoutSessionStatusEnum.CHECKOUT_SESSION_STATUS_FAILED ||
          paymentStatus.includes('fail')
        ) {
          setState('failed')
          setStatusText('Payment could not be completed.')
          trackPaymentEvent('failed', { sessionId: search.session_id, invoiceId, scheduleId, reason: paymentStatus })
          return
        }

        if (sessionStatus.status === CheckoutSessionStatusEnum.CHECKOUT_SESSION_STATUS_EXPIRED) {
          setState('expired')
          setStatusText('This checkout session expired.')
          trackPaymentEvent('failed', { sessionId: search.session_id, invoiceId, scheduleId, reason: 'session_expired' })
          return
        }

        const isCompleteSession = sessionStatus.status === CheckoutSessionStatusEnum.CHECKOUT_SESSION_STATUS_COMPLETE
        const isAsyncPaymentSubmitted = isCompleteSession && paymentStatus === 'unpaid'

        if (
          updatedSchedule?.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAYMENT_INITIATED &&
          isAsyncPaymentSubmitted
        ) {
          setState('submitted')
          setStatusText('Bank payment submitted. Confirmation can take 1-4 business days.')
          return
        }

        if (
          isCompleteSession &&
          Date.now() - startedAtRef.current > SESSION_SETTLE_TIMEOUT_MS
        ) {
          setState('submitted')
          setStatusText('Payment submitted. Confirmation may take a few minutes.')
          return
        }

        setState('processing')
        setStatusText(
          isCompleteSession
            ? 'Payment submitted. Waiting for confirmation...'
            : 'Waiting for payment completion...',
        )
        timeoutId = setTimeout(poll, SESSION_POLL_INTERVAL_MS)
      } catch (error) {
        if (!isMounted) return
        console.error('Failed to poll payment status:', error)
        captureError(error instanceof Error ? error : new Error(String(error)), {
          context: 'booking-processing',
          action: 'poll-session',
          sessionId: search.session_id,
          invoiceId,
          scheduleId,
        })
        setStatusText('Still checking payment status...')
        timeoutId = setTimeout(poll, SESSION_POLL_INTERVAL_MS)
      }
    }

    poll()

    return () => {
      isMounted = false
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [getCheckoutSessionStatus, invoiceId, refreshInvoice, scheduleId, search.session_id, tipOnly])

  useEffect(() => {
    if (!search.verification_url || !invoiceId || !scheduleId || state !== 'verification') return

    const intervalId = setInterval(() => {
      refreshInvoice().catch(error => {
        console.error('Failed to refresh invoice during bank verification:', error)
        captureError(error instanceof Error ? error : new Error(String(error)), {
          context: 'booking-processing',
          action: 'poll-bank-verification',
          invoiceId,
          scheduleId,
        })
      })
    }, VERIFICATION_POLL_INTERVAL_MS)

    return () => clearInterval(intervalId)
  }, [invoiceId, refreshInvoice, scheduleId, search.verification_url, state])

  useEffect(() => {
    if (!schedule) return
    if (schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAID) {
      setState('success')
      setStatusText('Payment confirmed.')
    } else if (
      schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAYMENT_INITIATED &&
      (search.verification_url || !search.session_id)
    ) {
      setState('submitted')
      setStatusText('Bank payment submitted. Confirmation can take 1-4 business days.')
    }
  }, [schedule, search.session_id, search.verification_url])

  useEffect(() => {
    if (state !== 'success' || successNavigateStartedRef.current) return
    const search = paymentSuccessBookingSearch({
      tipOnly,
      hasUnansweredQuestionnaire: booking?.questionnaire
        ? hasUnansweredQuestionnaireItems(booking.questionnaire)
        : false,
    })
    if (!search.panel) return
    successNavigateStartedRef.current = true
    navigate({
      to: '/booking/$id',
      params: { id },
      search,
      replace: true,
    })
  }, [booking?.questionnaire, id, navigate, state, tipOnly])

  const handleReturnToBooking = useCallback(() => {
    navigate({
      to: '/booking/$id',
      params: { id },
      search: {},
    })
  }, [id, navigate])

  const handleManualRefresh = useCallback(async () => {
    setIsRefreshing(true)
    try {
      await refreshInvoice()
    } finally {
      setIsRefreshing(false)
    }
  }, [refreshInvoice])

  const isWaitingForPayment = state === 'processing' || state === 'verification'
  const isLoading = isBookingLoading || !hasBookingLoaded || (invoiceId && isInvoiceLoading)
  const paymentTotals = invoice ? getCustomerFacingInvoicePaymentTotals(invoice) : null

  if (isLoading) {
    return <ProcessingSkeleton />
  }

  return (
    <div className="min-h-svh bg-background">
      <PortalHeader
        orgName={booking?.organization?.name || 'Payment status'}
        logoUrl={booking?.organization?.logoUrl}
        contactEmail={booking?.organization?.contactEmail}
        hasGallery={!!booking?.organization?.galleryImages?.length}
        backLabel="Return to booking"
        onBack={handleReturnToBooking}
        maxWidth="5xl"
      />

      <main className="mx-auto grid max-w-5xl gap-4 px-4 py-4 sm:gap-6 sm:py-8 md:px-8 lg:grid-cols-[minmax(280px,320px)_minmax(0,1fr)]">
        <section className="rounded-lg border bg-card p-5 sm:p-6 md:p-8 lg:col-start-2 lg:row-start-1">
          <StatusHero state={state} statusText={statusText} verificationUrl={search.verification_url} />

          <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center [&>*]:min-h-11 sm:[&>*]:min-h-9">
            {state === 'verification' && search.verification_url && (
              <Button asChild>
                <a href={search.verification_url} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-4 w-4" />
                  Open bank verification
                </a>
              </Button>
            )}
            {isWaitingForPayment && (
              <Button variant="outline" onClick={handleManualRefresh} disabled={isRefreshing}>
                {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Refresh status
              </Button>
            )}
            <Button variant={isWaitingForPayment ? 'secondary' : 'default'} onClick={handleReturnToBooking}>
              Return to booking
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          {state === 'verification' && (
            <p className="mt-4 text-sm text-muted-foreground">
              Bank verification opens with Stripe in a separate secure page. After finishing, return to your booking
              to track the processing status.
            </p>
          )}
        </section>

        <aside className="space-y-4 lg:col-start-1 lg:row-start-1 lg:sticky lg:top-24 lg:h-fit">
          <div className="rounded-lg border bg-card p-4">
            <h2 className="mb-3 text-sm font-semibold">Payment</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <span className="min-w-0 text-muted-foreground">{schedule?.description || 'Invoice payment'}</span>
                <span className="shrink-0 font-semibold font-financial">{formatCents(Number(amountDueCents))}</span>
              </div>
            </div>
          </div>

          {invoice && (
            <div className="rounded-lg border bg-card p-4">
              <h2 className="mb-3 text-sm font-semibold">{invoice.title || 'Invoice'}</h2>
              <div className="space-y-2 text-sm">
                <SummaryRow label="Total" value={formatCents(Number(invoice.totalCents + invoice.tipCents))} />
                {paymentTotals && paymentTotals.paidCents > 0n && (
                  <>
                    <SummaryRow label="Paid" value={formatCents(Number(paymentTotals.paidCents))} />
                    {paymentTotals.tipPaidCents > 0n && <TipPaidNote tipPaidCents={paymentTotals.tipPaidCents} />}
                  </>
                )}
                <SummaryRow label="Balance due" value={formatCents(Number(invoice.balanceDueCents))} strong />
              </div>
            </div>
          )}
        </aside>
      </main>

      {booking?.organization && <PortalFooter organization={booking.organization} />}
    </div>
  )
}

function StatusHero({
  state,
  statusText,
  verificationUrl,
}: {
  state: ProcessingState
  statusText: string
  verificationUrl?: string
}) {
  if (state === 'success') {
    return (
      <div className="text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
        <h1 className="mt-4 text-2xl font-semibold">Payment confirmed</h1>
        <p className="mt-2 text-sm text-muted-foreground">Your receipt will be sent by email.</p>
      </div>
    )
  }

  if (state === 'submitted') {
    return (
      <div className="text-center">
        <ShieldCheck className="mx-auto h-12 w-12 text-blue-600" />
        <h1 className="mt-4 text-2xl font-semibold">Payment submitted</h1>
        <p className="mt-2 text-sm text-muted-foreground">{statusText}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          You can return to your booking and track the status there.
        </p>
      </div>
    )
  }

  if (state === 'failed' || state === 'expired') {
    return (
      <div className="text-center">
        <AlertTriangle className="mx-auto h-12 w-12 text-destructive" />
        <h1 className="mt-4 text-2xl font-semibold">
          {state === 'expired' ? 'Session expired' : 'Payment failed'}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">{statusText}</p>
      </div>
    )
  }

  if (state === 'unavailable') {
    return (
      <div className="text-center">
        <AlertTriangle className="mx-auto h-12 w-12 text-amber-600" />
        <h1 className="mt-4 text-2xl font-semibold">Payment status unavailable</h1>
        <p className="mt-2 text-sm text-muted-foreground">{statusText}</p>
      </div>
    )
  }

  if (state === 'verification') {
    return (
      <div className="text-center">
        <ShieldCheck className="mx-auto h-12 w-12 text-amber-600" />
        <h1 className="mt-4 text-2xl font-semibold">Verify your bank account</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {verificationUrl
            ? 'Stripe needs one more step before this bank payment can process.'
            : 'Waiting for bank verification details.'}
        </p>
      </div>
    )
  }

  return (
    <div className="text-center">
      <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
      <h1 className="mt-4 text-2xl font-semibold">Processing payment</h1>
      <p className="mt-2 text-sm text-muted-foreground">{statusText}</p>
      <p className="mt-1 text-sm text-muted-foreground">
        You can return to your booking while confirmation continues.
      </p>
    </div>
  )
}

function TipPaidNote({ tipPaidCents }: { tipPaidCents: bigint }) {
  return (
    <div className="-mt-1 text-right text-xs text-muted-foreground font-financial">
      incl. {formatCents(Number(tipPaidCents))} tip
    </div>
  )
}

function SummaryRow({
  label,
  value,
  strong = false,
}: {
  label: string
  value: string
  strong?: boolean
}) {
  return (
    <div className={strong ? 'flex justify-between font-semibold' : 'flex justify-between'}>
      <span className="text-muted-foreground">{label}</span>
      <span className="font-financial">{value}</span>
    </div>
  )
}

function ProcessingSkeleton() {
  return (
    <div className="min-h-svh">
      <header className="z-10 border-b border-border bg-card md:sticky md:top-0">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3 sm:py-4 md:px-8">
          <SkeletonBar className="h-10 w-52" />
          <SkeletonBar className="h-9 w-36" />
        </div>
      </header>
      <main className="mx-auto grid max-w-5xl gap-4 px-4 py-4 sm:gap-6 sm:py-8 md:px-8 lg:grid-cols-[minmax(280px,320px)_minmax(0,1fr)]">
        <SkeletonBar className="h-80 w-full lg:col-start-2 lg:row-start-1" />
        <div className="space-y-4 lg:col-start-1 lg:row-start-1">
          <SkeletonBar className="h-32 w-full" />
          <SkeletonBar className="h-40 w-full" />
        </div>
      </main>
    </div>
  )
}

function getScheduleAmountDue(schedule: NonNullable<PublicInvoice['paymentSchedules']>[number]) {
  const goodwillRefundedCents = schedule.goodwillRefundedCents || 0n
  return (schedule.amountWithTipCents ?? schedule.amountCents) - goodwillRefundedCents - schedule.amountPaidCents
}
